import type {
  ChessboardActionLogDto,
  ChessboardActionPayload,
  ChessboardDto,
  ChessMoveDto,
  CreateChessboardPayload,
  MakeMovePayload,
  MyBoardsDto,
  VettedLocationDto,
} from '@/core/data/chessboard/chessboard.interface';
import { ChessboardEndpoint } from '@/core/network/api/constants/api.url.constant';
import { HttpClient } from '@/core/network/http/httpClient';
import type { GamResponse } from '@/core/network/http/httpClient.interface';
import type { AxiosResponse } from 'axios';

export class ChessboardApi {
  private readonly httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient;
  }

  async getBoard(id: string): Promise<AxiosResponse<GamResponse<ChessboardDto>>> {
    return this.httpClient.get<ChessboardDto>(ChessboardEndpoint.GET_BOARD, {
      urlParams: { id },
    });
  }

  async getMyBoards(): Promise<AxiosResponse<GamResponse<MyBoardsDto>>> {
    return this.httpClient.get<MyBoardsDto>(ChessboardEndpoint.GET_MY_BOARDS);
  }

  async getWorldBoards(): Promise<AxiosResponse<GamResponse<ChessboardDto[]>>> {
    return this.httpClient.get<ChessboardDto[]>(ChessboardEndpoint.GET_WORLD_BOARDS);
  }

  async getAllBoards(): Promise<AxiosResponse<GamResponse<ChessboardDto[]>>> {
    return this.httpClient.get<ChessboardDto[]>(ChessboardEndpoint.GET_ALL_BOARDS);
  }

  async getVettedLocations(): Promise<AxiosResponse<GamResponse<VettedLocationDto[]>>> {
    return this.httpClient.get<VettedLocationDto[]>(ChessboardEndpoint.GET_VETTED_LOCATIONS);
  }

  async getActiveByLocation(locationId: string): Promise<AxiosResponse<GamResponse<ChessboardDto[]>>> {
    return this.httpClient.get<ChessboardDto[]>(ChessboardEndpoint.GET_ACTIVE_BY_LOCATION, {
      urlParams: { locationId },
    });
  }

  async getCompletedByLocation(locationId: string): Promise<AxiosResponse<GamResponse<ChessboardDto[]>>> {
    return this.httpClient.get<ChessboardDto[]>(ChessboardEndpoint.GET_COMPLETED_BY_LOCATION, {
      urlParams: { locationId },
    });
  }

  async getMoves(id: string): Promise<AxiosResponse<GamResponse<ChessMoveDto[]>>> {
    return this.httpClient.get<ChessMoveDto[]>(ChessboardEndpoint.GET_MOVES, {
      urlParams: { id },
    });
  }

  async getActionLogs(id: string): Promise<AxiosResponse<GamResponse<ChessboardActionLogDto[]>>> {
    return this.httpClient.get<ChessboardActionLogDto[]>(ChessboardEndpoint.GET_ACTION_LOGS, {
      urlParams: { id },
    });
  }

  async create(payload: CreateChessboardPayload): Promise<AxiosResponse<GamResponse<ChessboardDto>>> {
    return this.httpClient.post<ChessboardDto, CreateChessboardPayload>(ChessboardEndpoint.CREATE, {
      data: payload,
    });
  }

  async makeMove(id: string, payload: MakeMovePayload): Promise<AxiosResponse<GamResponse<ChessboardDto>>> {
    return this.httpClient.post<ChessboardDto, MakeMovePayload>(ChessboardEndpoint.MAKE_MOVE, {
      urlParams: { id },
      data: payload,
    });
  }

  async performAction(
    id: string,
    payload: ChessboardActionPayload,
  ): Promise<AxiosResponse<GamResponse<ChessboardDto>>> {
    return this.httpClient.post<ChessboardDto, ChessboardActionPayload>(ChessboardEndpoint.PERFORM_ACTION, {
      urlParams: { id },
      data: payload,
    });
  }

  async createFirestoreDoc(id: string): Promise<AxiosResponse<GamResponse<any>>> {
    return await this.httpClient.put(ChessboardEndpoint.FIRESTORE, {
      urlParams: { id },
    });
  }

  async abandon(id: string): Promise<AxiosResponse<GamResponse<ChessboardDto>>> {
    return this.httpClient.put(ChessboardEndpoint.ABANDON, {
      urlParams: { id },
    });
  }

  async deleteBoard(id: string): Promise<AxiosResponse<GamResponse<void>>> {
    return this.httpClient.delete<void>(ChessboardEndpoint.DELETE_BOARD, {
      urlParams: { id },
    });
  }
}
